import { useParams, Navigate, Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { CheckCircle2, ArrowRight, MapPin } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CtaBanner } from "./Home";
import { PROJECTS, slugify } from "../data/site";

const BackgroundVideo = ({ src }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      data-testid="project-video"
      src={src}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      className="h-auto w-auto max-h-[80vh] max-w-full block object-contain"
    />
  );
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => slugify(p.title) === slug);
  if (!project) return <Navigate to="/projects" replace />;
  const more = PROJECTS.filter((p) => slugify(p.title) !== slug).slice(0, 3);

  return (
    <div data-testid="project-detail-page">
      <PageHero eyebrow={project.type?.toUpperCase()} title={project.title}
        subtitle={project.location} image={project.image}
        crumbs={[{ label: "Projects", to: "/projects" }, { label: project.title }]} />

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-12">
          <Reveal className="lg:col-span-2">
            <span className="text-js-blue font-semibold text-sm tracking-wide">PROJECT OVERVIEW</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-3 mb-5">Mechanical services, delivered end-to-end</h2>
            <p className="text-js-charcoal/60 leading-relaxed text-lg">{project.description || project.solution}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-js-grey rounded-3xl p-8 border border-black/5">
              <h3 className="font-display font-bold text-lg mb-5">Project Scope</h3>
              <dl className="space-y-4">
                {(project.scope || [
                  project.client && { label: "Client", value: project.client },
                  project.type && { label: "Project Type", value: project.type },
                  project.status && { label: "Status", value: project.status },
                  { label: "Location", value: project.location },
                  project.services && { label: "Services", value: project.services.join(" · ") },
                ].filter(Boolean)).map((s) => (
                  <div key={s.label} className="border-b border-black/10 pb-3 last:border-0 last:pb-0">
                    <dt className="text-xs text-js-charcoal/50 font-semibold tracking-wide">{s.label.toUpperCase()}</dt>
                    <dd className="font-semibold text-js-charcoal mt-0.5">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      {project.installations && (
        <section className="pb-4 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal>
              <h3 className="font-display font-bold text-2xl mb-6">Systems Installed</h3>
              <div className="overflow-hidden rounded-3xl border border-black/10">
                <table className="w-full text-left border-collapse" data-testid="installations-table">
                  <thead>
                    <tr className="bg-js-blue text-white">
                      <th className="px-5 md:px-8 py-4 font-display font-semibold text-sm tracking-wide">Installation</th>
                      <th className="px-5 md:px-8 py-4 font-display font-semibold text-sm tracking-wide">Brand</th>
                      <th className="px-5 md:px-8 py-4 font-display font-semibold text-sm tracking-wide">Product</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.installations.map((r, i) => (
                      <tr key={i} className={`${i % 2 ? "bg-js-grey" : "bg-white"} border-t border-black/5`}>
                        <td className="px-5 md:px-8 py-4 text-js-charcoal/70 text-sm">{r.name}</td>
                        <td className="px-5 md:px-8 py-4 font-semibold text-js-charcoal text-sm">{r.brand}</td>
                        <td className="px-5 md:px-8 py-4 text-js-charcoal/80 text-sm">{r.product}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {project.video && (
        <section className="pt-16 pb-4 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal className="mb-8">
              <span className="text-js-red font-semibold text-xs tracking-wide">PROJECT FILM</span>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">See the installation in motion</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden rounded-3xl border border-black/10 bg-js-charcoal shadow-xl flex justify-center">
                <BackgroundVideo src={project.video} />
                <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 rounded-full bg-black/40 backdrop-blur px-3 py-1.5 pointer-events-none">
                  <span className="w-1.5 h-1.5 rounded-full bg-js-red animate-pulse" />
                  <span className="text-white text-[11px] font-semibold tracking-wide">SHOWREEL</span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {project.serviceBlocks && (
        <section className="pb-8 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-6">
            {project.serviceBlocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <div className="grid md:grid-cols-2 rounded-3xl overflow-hidden border border-black/5 bg-js-grey">
                  <div className={`relative min-h-[280px] ${i % 2 ? "md:order-2" : ""}`}>
                    <img src={b.image} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="p-8 md:p-12">
                    <span className="text-js-red font-semibold text-xs tracking-wide">0{i + 1}</span>
                    <h3 className="font-display font-extrabold text-2xl tracking-tight mt-2">{b.title}</h3>
                    <p className="text-js-charcoal/60 mt-3 leading-relaxed">{b.intro}</p>
                    <ul className="mt-5 space-y-2.5">
                      {b.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm"><CheckCircle2 size={18} className="text-js-blue mt-0.5 shrink-0" /><span className="text-js-charcoal/70">{p}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {project.gallery && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal className="mb-10"><h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Project gallery</h2></Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {project.gallery.map((g, i) => (
                <Reveal key={i} delay={(i % 3) * 0.05}>
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3] border border-black/5 group">
                    <img src={g} alt={`${project.title} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {!project.serviceBlocks && (
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            {project.services && (
              <div className="mb-8">
                <h3 className="font-display font-bold text-xl mb-4">Services provided</h3>
                <div className="flex flex-wrap gap-2.5">
                  {project.services.map((s) => (
                    <span key={s} className="px-4 py-2 rounded-full bg-js-blue/10 text-js-blue text-sm font-semibold">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {project.challenge && (
              <div className="max-w-4xl space-y-4">
                {[["Challenge", project.challenge], ["Solution", project.solution], ["Outcome", project.outcome]].map(([k, v]) => (
                  <div key={k} className="bg-js-grey rounded-2xl p-6 border border-black/5">
                    <p className="font-display font-bold text-js-blue mb-1">{k}</p>
                    <p className="text-js-charcoal/70">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h3 className="font-display font-bold text-2xl mb-8">More projects</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {more.map((r) => (
              <Link key={r.title} to={`/projects/${slugify(r.title)}`} className="group relative overflow-hidden rounded-3xl h-56 border border-black/5">
                <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-js-charcoal/90 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <span className="text-js-red text-xs font-semibold">{r.category}</span>
                  <h4 className="font-display font-bold text-lg flex items-center gap-2">{r.title} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" /></h4>
                  <p className="text-white/70 text-sm flex items-center gap-1.5 mt-0.5"><MapPin size={12} /> {r.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBanner />
    </div>
  );
};

export default ProjectDetail;
